import { Box } from '@chakra-ui/react'
import {
  ContentNotFound,
  DetailPageNavigationHeader,
  ExhibitionAndEventAbout,
  ExhibitionAndEventDaysAndTickets,
  ExhibitionAndEventDetailHeroCard,
  Hero,
  Layout,
  PhotoAndVideoSliderSection,
  RelatedEvents,
  StickyHeader,
  ThreeDTourEmbedVideo,
} from '@common'
import { Link as LinkIcon } from '@icon'
import { ISSRPageData } from '@interfaces'
import { useShare } from '@lib/hooks'
import { EventService } from '@services'
import { Link as SSMLink } from '@ui'
import { checkStickyNavItem, createNavLink, getStickyId } from '@utils'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

const EventDetailScreen = ({
  pageData,
  global: { navigation },
}: ISSRPageData) => {
  const { t } = useTranslation()
  const { asPath, locale, basePath } = useRouter()

  if (pageData.error) {
    return (
      <Layout
        title={t('common:contentNotFound')}
        navigation={navigation}
        path={asPath}
      >
        <ContentNotFound />
      </Layout>
    )
  }
  //let webpimgUrl = pageData.data.heroCard.bgImgUrl.replace('files','files/styles/webp/public') + '.webp';
  //console.log(pageData.data.heroCard.bgImgUrl);
  return (
    <Layout
      title={pageData.data.heroCard.title}
      navigation={navigation}
      path={asPath}
    >
      <Hero
        mobileBgImageUrl={pageData.data.heroCard.mobileBgImgUrl}
        bgImageUrl={pageData.data.heroCard.bgImgUrl}
        headerNav={
          <DetailPageNavigationHeader
            leftButtonLabel={t('common:goBackButtonLabel')}
            leftButtonHref={'#'}
            isWithBackButton={true}
            rightButtonLabel={t(
              'exhibition-and-event:eventAndExhibitionDetail.share'
            )}
            rightButtonAction={useShare}
            isRightButtonShareAction={true}
            shareProps={{
              title: `${pageData.data.heroCard.title} | SSM`,
              text: pageData.data.heroCard.title,
              url: basePath,
            }}
          />
        }
        isCoveredBg={false}
      ></Hero>
      <ExhibitionAndEventDetailHeroCard
        title={pageData.data.heroCard.title}
        date={pageData.data.heroCard.date}
        displayBuyTicketButtonGroup={false}
        displayFooterButtonGroup={false}
      />
      <style jsx global>{`
        .css-5s4db3 div > div > a {
          color: #c71f3f;
        }
      `}</style>
      <Box as="main" pt={4}>
        <StickyHeader navs={pageData.data.stickyNavItems}>
          {pageData.data?.pressRelease && (
            <SSMLink
              display="flex"
              alignItems="center"
              fontSize="sm"
              fontWeight="light"
              color="black.primary3"
              _hover={{ cursor: 'pointer' }}
              position="relative"
              whiteSpace="pre"
              href={`${createNavLink('press', locale)}/${
                pageData.data?.pressRelease
              }`}
            >
              {t('nav:stickyItems.pressRelease')}
              <LinkIcon ml="10px" width="15px" height="15px" />
            </SSMLink>
          )}

          {pageData.data?.googleArtLink && (
            <SSMLink
              display="flex"
              alignItems="center"
              fontSize="sm"
              fontWeight="light"
              color="black.primary3"
              _hover={{ cursor: 'pointer' }}
              position="relative"
              whiteSpace="pre"
              href={pageData.data?.googleArtLink}
              target="_blank"
            >
              Google Arts & Culture
              <LinkIcon ml="10px" width="15px" height="15px" />
            </SSMLink>
          )}
        </StickyHeader>
        {checkStickyNavItem(pageData.data.stickyNavItems, 'aboutTheEvent') && (
          <ExhibitionAndEventAbout
            sectionId={getStickyId(
              pageData.data.stickyNavItems,
              'aboutTheEvent'
            )}
            stickyTitle={pageData.data.eventAbout.stickyTitle}
            staticDescriptions={pageData.data.eventAbout.description}
            artists={pageData.data.artists}
            activePage="event"
          />
        )}
        {checkStickyNavItem(pageData.data.stickyNavItems, 'daysAndTickets') && (
          <ExhibitionAndEventDaysAndTickets
            tickets={pageData.data.products}
            sectionId={getStickyId(
              pageData.data.stickyNavItems,
              'daysAndTickets'
            )}
          />
        )}
        {checkStickyNavItem(
          pageData.data.stickyNavItems,
          'photosAndVideos'
        ) && (
          <PhotoAndVideoSliderSection
            data={pageData.data.photosAndVideos}
            stickyId={getStickyId(
              pageData.data.stickyNavItems,
              'photosAndVideos'
            )}
            sectionTitle={t('nav:stickyItems.photosAndVideos')}
            pb={{ base: 'padding.primary2', lg: 'padding.primary4' }}
            my="0"
          />
        )}
        {checkStickyNavItem(pageData.data.stickyNavItems, 'threeDTour') && (
          <ThreeDTourEmbedVideo
            stickyId={getStickyId(pageData.data.stickyNavItems, 'threeDTour')}
            embedUrl={pageData.data?.threeDTourEmbedLink}
          />
        )}
        {checkStickyNavItem(pageData.data.stickyNavItems, 'otherEvents') && (
          <RelatedEvents
            stickyId={getStickyId(pageData.data.stickyNavItems, 'otherEvents')}
            title={t('nav:stickyItems.otherEvents')}
            collection={pageData.data.otherEvents}
            containerProps={{
              mt: { base: 'padding.primary2', lg: 'padding.primary4' },
            }}
          />
        )}
      </Box>
    </Layout>
  )
}

export default EventDetailScreen

export async function getServerSideProps({ query, locale }) {
  const { slug } = query
  const pageData = {
    data: null,
    error: null,
  }

  try {
    const collection = await EventService.getEventDetailPage(slug, locale)

    pageData.data = collection
  } catch (err) {
    if (err instanceof Error) {
      pageData.error = { message: err.message }
    }
  }

  return {
    props: { pageData },
  }
}
